import {CSSProperties, useState} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, useBoolean} from 'shared/web';

import {useElementSize} from '../../hooks/useElementSize';
import {AdaptiveImageProps} from '../../types/AdaptiveImageProps';
import {getCssSize} from '../../utils/getCssSize';
import {Box} from '../Box/Box';
import {Center} from '../Center/Center';
import {Hide} from '../Hide/Hide';
import {Icon} from '../Icon/Icon';
import {Show} from '../Show/Show';
import {Spinner} from '../Spinner/Spinner';

export type {AdaptiveImageProps} from '../../types/AdaptiveImageProps';
export type {CommonImageProps} from '../../types/CommonImageProps';
export type {RatioImageProps} from '../../types/RatioImageProps';
export type {SizedImageProps} from '../../types/SizedImageProps';

export function AdaptiveImage(props: AdaptiveImageProps) {
  const [isLoading, , finishLoading] = useBoolean(true);
  const [ref, width, height] = useElementSize<HTMLImageElement>({debounce: 1000});
  const [imageErrorState, setImageErrorState] = useState({url: props.url, isError: false});

  if (imageErrorState.url !== props.url) {
    setImageErrorState({url: props.url, isError: false});
  }

  const fit = props.fit ?? 'contain';

  const hasHeight = height && height > 0;

  const src =
    isNotNil(props.url) && isNotNil(width) && width > 0 && isNotNil(height) && height > 0
      ? props.makeUrl(props.url, normalizeSize(width), normalizeSize(height), fit)
      : undefined;
  const shouldShowImage = isNotNil(props.url) && !imageErrorState.isError;

  const imageStyle: CSSProperties = {
    objectFit: fit,
    objectPosition: props.position,
    aspectRatio: props.ratio,
    width: getCssSize(props.width ?? '100%'),
    height: getCssSize(props.height ?? '100%'),
    opacity: isLoading ? 0 : 1,
    display: shouldShowImage ? undefined : 'none',
  };

  return (
    <Box
      width={props.width}
      height={props.height}
      maxWidth={props.maxWidth}
      maxHeight={props.maxHeight}
      position="relative"
    >
      <img
        ref={ref}
        alt={props.alt}
        src={props.shouldUseOriginal ? props.url : src}
        loading={props.isLazy ? 'lazy' : undefined}
        className="overflow-hidden"
        style={imageStyle}
        onError={(event) => {
          finishLoading();
          setImageErrorState({url: props.url, isError: true});
          props.onError?.(event);
        }}
        onLoad={(event) => {
          finishLoading();
          props.onLoad?.(event);
        }}
        data-testid={suffixTestId('image', props)}
      />

      <Hide when={shouldShowImage}>
        <Box
          width={props.width}
          height={props.height}
          maxWidth={props.maxWidth}
          maxHeight={props.maxHeight}
        >
          <Center width="100%" height="100%">
            {isNotNil(props.placeholder) ? (
              props.placeholder
            ) : (
              <Icon color="palettes.neutral.100.100" value="image/photo" size={11} />
            )}
          </Center>
        </Box>
      </Hide>
      <Show when={isLoading && isNotNil(props.url)}>
        <Box
          {...(hasHeight && {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          })}
        >
          <Center width="100%" height="100%">
            <Spinner data-testid={suffixTestId('loading-placeholder', props)} />
          </Center>
        </Box>
      </Show>
    </Box>
  );
}

const normalizeSize = (size: number | null) =>
  Math.round((size ?? 0) * (window?.devicePixelRatio ?? 1));
